import React from 'react';
import HighchartsHistogram from 'highcharts/modules/histogram-bellcurve';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { useTranslation } from 'react-i18next';
import { StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import { adjustColor, generatePoints } from '@/summaryReport/summaryReport.utilities';

export interface HistogramProps {
  items: StatisticsSummaryItem[];
  title?: string;
  xAxisTitle?: string;
  yAxisTitle?: string;
}

export const SummaryHistogram: React.FunctionComponent<HistogramProps> = ({ items, title, xAxisTitle, yAxisTitle }) => {
  // use overallMin and Max in case normal distribution curve is overlaid for multiple items
  const overallMin = Math.min(...items.map((item) => item.data.stats.min));
  const overallMax = Math.max(...items.map((item) => item.data.stats.max));
  const { t } = useTranslation();
  const opacity = 0.6 + 0.4 / items.length;

  const tooltipFormatter = function (this: any) {
    const binStart = this.x;
    const seriesOptions = this.series.options as any;
    const binEnd = this.x + seriesOptions.binWidth;
    const yValue = this.y ?? 0;
    const actualCount = yValue * seriesOptions.totalCount * seriesOptions.binWidth;
    const percentage = ((actualCount / seriesOptions.totalCount) * 100).toFixed(2);

    return `<b>${this.series.name}</b><br/>
      Bin: ${binStart.toFixed(2)} - ${binEnd.toFixed(2)}<br/>
      Count: ${actualCount.toFixed(0)}<br/>
      Percentage: ${percentage}%`;
  };

  const series = items.map((item) => ({
    name: item.name,
    data: item.data.histogram.map(([start, _, count]) => [
      start,
      //scaling to match bellcurve,
      item.data.stats.count * item.data.inputs.histogramBinWidth !== 0
        ? count / (item.data.stats.count * item.data.inputs.histogramBinWidth)
        : 0,
    ]),
    type: 'histogram' as const,
    color: item.color,
    showInLegend: true,
    opacity,
    totalCount: item.data.stats.count,
    binWidth: item.data.inputs.histogramBinWidth,
    tooltip: {
      pointFormatter: tooltipFormatter,
    },
  }));

  const bellCurveSeries = items.map((item) => {
    const bellCurveData = generatePoints(
      item.data.stats.average,
      item.data.stats.stdDev,
      overallMin,
      overallMax,
      item.data.inputs.histogramBinWidth,
    );

    return {
      name: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.NORMAL_DISTRIBUTION_NAME', { name: item.name }),
      data: bellCurveData,
      type: 'bellcurve' as const,
      color: adjustColor(item.color, 40, false),
      fillOpacity: 0.1,
      showInLegend: false,
      marker: {
        enabled: false,
      },
    };
  });

  const options: Highcharts.Options = {
    chart: {
      type: 'histogram',
      height: '80%',
      animation: false,
    },
    title: {
      text: title,
      align: 'left',
      style: {
        fontSize: 'small',
        fontWeight: 'normal',
      },
    },
    legend: {
      enabled: items.length > 1,
      verticalAlign: 'top',
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      plotLines: [
        {
          color: 'red',
          width: 1,
          value: items[0].data.inputs.lowerSpecificationLimit,
          zIndex: items.length + 2,
          dashStyle: 'Dash',
          label: {
            text: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.LSL'),
            rotation: 0,
            style: {
              color: 'black',
              fontSize: 'small',
            },
          },
        },
        {
          color: 'green',
          width: 1,
          value: items[0].data.inputs.target,
          zIndex: items.length + 2,
          dashStyle: 'Dash',
          label: {
            text: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.TARGET'),
            rotation: 0,
            style: {
              color: 'black',
              fontSize: 'small',
            },
          },
        },
        {
          color: 'red',
          width: 1,
          value: items[0].data.inputs.upperSpecificationLimit,
          zIndex: items.length + 2,
          dashStyle: 'Dash',
          label: {
            text: t('INVESTIGATE_TOOLS.SUMMARY_REPORT.USL'),
            rotation: 0,
            style: {
              color: 'black',
              fontSize: 'small',
            },
          },
        },
      ],
      title: {
        text: xAxisTitle,
      },
      min: overallMin,
    },
    yAxis: {
      title: {
        text: yAxisTitle,
      },
    },

    plotOptions: {
      histogram: {
        animation: false,
      },
    },
    series: [...series, ...bellCurveSeries] as any,
  };

  return <HighchartsReact highcharts={HighchartsHistogram} options={options} />;
};
